/* eslint-disable jsdoc/require-jsdoc */
import { darken, lighten } from '@osmosis/shared/styles/colorModifiers';
import {
  QuizAccuracy,
  QuizConfidence,
  QuizItem,
  QuizItemTime,
  QuizProgressItem,
} from '../../../types/quiz';
import { Filter } from '../../../components/quiz/useQuizProgress';
import dayjs from 'dayjs';
import { EstimatedTimePerQuestion, QuizTypeGroup } from '../../../constants';
import { ColorProps } from '@osmosis/shared/styles';
import { IntlShape } from 'react-intl';

const COLOR_STEP = 0.05;

export const getEstimatedTime = (
  count: number,
  quizType: QuizTypeGroup = 'NA',
) => {
  return Math.round(EstimatedTimePerQuestion[quizType] * count);
};

const getColorFunc = (step: number) => (colors: ColorProps) =>
  darken(COLOR_STEP * step, lighten(0.3, colors.primary));

const weeksMessage = (intl: IntlShape, count: number) =>
  intl.formatMessage(
    {
      id: 'quiz.time.weeks',
      defaultMessage:
        '{count, plural, one {{count} week} other {{count} weeks}} ago',
    },
    {
      count,
    },
  );

export const quizTimes = (intl: IntlShape) =>
  [
    {
      text: intl.formatMessage({ id: 'quiz.time.na', defaultMessage: 'N/A' }),
      cutoff: -1,
      /** . */
      color: () => 'transparent',
      id: QuizItemTime.NA,
    },
    {
      text: intl.formatMessage({
        id: 'quiz.time.today',
        defaultMessage: 'Today',
      }),
      cutoff: 1,
      color: getColorFunc(0),
      id: QuizItemTime.TODAY,
    },
    {
      text: intl.formatMessage({
        id: 'quiz.time.yesterday',
        defaultMessage: 'Yesterday',
      }),
      cutoff: 2,
      color: getColorFunc(1),
      id: QuizItemTime.YESTERDAY,
    },
    {
      text: intl.formatMessage({
        id: 'quiz.time.fourDaysAgo',
        defaultMessage: '4 days ago',
      }),
      cutoff: 4,
      color: getColorFunc(2),
      id: QuizItemTime.FOUR_DAYS_AGO,
    },
    {
      text: weeksMessage(intl, 1),
      cutoff: 7,
      color: getColorFunc(3),
      id: QuizItemTime.ONE_WEEK_AGO,
    },
    {
      text: weeksMessage(intl, 2),
      cutoff: 14,
      color: getColorFunc(4),
      id: QuizItemTime.TWO_WEEKS_AGO,
    },
    {
      text: weeksMessage(intl, 3),
      cutoff: 21,
      color: getColorFunc(5),
      id: QuizItemTime.THREE_WEEKS_AGO,
    },
    {
      text: weeksMessage(intl, 4),
      cutoff: 28,
      color: getColorFunc(6),
      id: QuizItemTime.FOUR_WEEKS_AGO,
    },
    {
      text: weeksMessage(intl, 8),
      cutoff: 56,
      color: getColorFunc(7),
      id: QuizItemTime.EIGHT_WEEKS_AGO,
    },
    {
      text: intl.formatMessage({
        id: 'quiz.time.greater_than_two_months_ago',
        defaultMessage: '> 2 months ago',
      }),
      cutoff: 1825,
      color: getColorFunc(8),
      id: QuizItemTime.TWO_MONTHS_AGO,
    },
  ].sort((a, b) => a.cutoff - b.cutoff);

type QuizItemObject = { [k in QuizItemTime]?: QuizItem[] };
export const groupQuizItems = (
  intl: IntlShape,
  items: QuizProgressItem[],
): QuizItemObject => {
  return items.reduce((obj: QuizItemObject, current) => {
    // accuracy can be 0, so we need to check if it's undefined
    if (current.accuracy === undefined) {
      return {
        ...obj,
        [QuizItemTime.NA]: [...(obj[QuizItemTime.NA] || []), current],
      } as QuizItemObject;
    }
    const itemDate = dayjs(current.time);
    const todaysDate = dayjs();
    const time = quizTimes(intl).find((t) =>
      itemDate.isAfter(todaysDate.subtract(t.cutoff, 'days')),
    );

    return {
      ...obj,
      ...(time ? { [time.id]: [...(obj[time.id] || []), current] } : {}),
    };
  }, {});
};

export const outerSlices = (
  intl: IntlShape,
): {
  id: string;
  filterType: Filter;
  filter: (item: QuizProgressItem) => void;
  color: (colors: ColorProps) => string;
  text: string;
}[] => [
  {
    id: 'new',
    filterType: Filter.New,
    filter: (item) => !item.answered,
    color: (colors) => colors.gray20,
    text: intl.formatMessage({
      defaultMessage: 'new',
      id: 'quiz_scoreboard.new',
    }),
  },
  {
    id: 'correct-with-high-confidence',
    filterType: Filter.CorrectHigh,
    filter: (item) =>
      item.accuracy === QuizAccuracy.CORRECT &&
      item.confidence === QuizConfidence.HIGH,
    color: (colors) => colors.green,
    text: intl.formatMessage({
      defaultMessage: 'correct with high confidence',
      id: 'quiz_scoreboard.correct_with_high_confidence',
    }),
  },
  {
    id: 'correct-with-medium-confidence',
    filterType: Filter.CorrectMedium,
    filter: (item) =>
      item.accuracy === QuizAccuracy.CORRECT &&
      item.confidence === QuizConfidence.MEDIUM,
    color: (colors) => darken(0.1, colors.green),
    text: intl.formatMessage({
      defaultMessage: 'correct with medium confidence',
      id: 'quiz_scoreboard.correct_with_medium_confidence',
    }),
  },
  {
    id: 'correct-with-low-confidence',
    filterType: Filter.CorrectLow,
    filter: (item) =>
      item.accuracy === QuizAccuracy.CORRECT &&
      item.confidence === QuizConfidence.LOW,
    color: (colors) => darken(0.2, colors.green),
    text: intl.formatMessage({
      defaultMessage: 'correct with low confidence',
      id: 'quiz_scoreboard.correct_with_low_confidence',
    }),
  },
  {
    id: 'incorrect-with-high-confidence',
    filterType: Filter.IncorrectHigh,
    filter: (item) =>
      item.accuracy === QuizAccuracy.INCORRECT &&
      item.confidence === QuizConfidence.HIGH,
    color: (colors) => darken(0.2, colors.red),
    text: intl.formatMessage({
      defaultMessage: 'incorrect with high confidence',
      id: 'quiz_scoreboard.incorrect_with_high_confidence',
    }),
  },
  {
    id: 'incorrect-with-medium-confidence',
    filterType: Filter.IncorrectMedium,
    filter: (item) =>
      item.accuracy === QuizAccuracy.INCORRECT &&
      item.confidence === QuizConfidence.MEDIUM,
    color: (colors) => darken(0.1, colors.red),
    text: intl.formatMessage({
      defaultMessage: 'incorrect with medium confidence',
      id: 'quiz_scoreboard.incorrect_with_medium_confidence',
    }),
  },
  {
    id: 'incorrect-with-low-confidence',
    filterType: Filter.IncorrectLow,
    filter: (item) =>
      item.accuracy === QuizAccuracy.INCORRECT &&
      item.confidence === QuizConfidence.LOW,
    color: (colors) => colors.red,
    text: intl.formatMessage({
      defaultMessage: 'incorrect with low confidence',
      id: 'quiz_scoreboard.incorrect_with_low_confidence',
    }),
  },
];
